import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {
  AllowanceEntity,
  BokkzAlgDataService,
  BokkzTslDataService,
  BokkzTslLogic,
  ClientEntity,
  Kind,
  OpvangPeriodes,
  OpvangPeriodesOpvangVorm,
  OpvangPeriodesSoortOpvang,
  Periode
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormArray, FormBuilder, FormGroup} from "@angular/forms";
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {filter, Subject, takeUntil} from "rxjs";
import {Event, NavigationEnd, Router} from "@angular/router";
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {SharedUiAlertModule} from "@snelstart/ui-alert";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-kinderopvangtoeslag',
  templateUrl: './kinderopvangtoeslag.component.html',
  styleUrls: ['./kinderopvangtoeslag.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    SharedUiDetailsModule,
    CommonModule,
    SharedUiAlertModule
  ]
})
export class KinderopvangtoeslagComponent implements OnInit, OnDestroy {

    @Input() public id!: string;
  private destroy$ = new Subject<void>();
  private clientEntity = {} as ClientEntity;
    private isSaving = false;
    private focusedElementId: string | null = null;
    public allowanceEntity = {} as AllowanceEntity;
    public kinderenFg = {} as FormGroup;
    public formStructure: any;
    public hasKinderen = false;
    public year: any;

    public optionsJaNee: SelectFormControlItem<boolean>[] = [
        {value: true, label: "Ja"},
        {value: false, label: "Nee"},
    ];

    public opvangVorm: SelectFormControlItem<string>[] = [
        {value: OpvangPeriodesOpvangVorm.GASTOUDEROPVANG, label: "Gastouderopvang"},
        {value: OpvangPeriodesOpvangVorm.KINDERCENTRUM, label: "Kindcentrum"},
    ];

    public opvangSoort: SelectFormControlItem<string>[] = [
        {value: OpvangPeriodesSoortOpvang.BUITENSCHOOLSE_OPVANG, label: "Buitenschoolse opvang"},
        {value: OpvangPeriodesSoortOpvang.DAGOPVANG, label: "Dagopvang"},
    ];

    constructor(
        private router: Router,
        public tslLogic: BokkzTslLogic,
        public dataService: BokkzTslDataService,
        public dataAlgService: BokkzAlgDataService,
        private fb: FormBuilder) {
        this.kinderenFg = this.fb.group({
            kinderen: this.fb.array<Kind>([])
        });
    }

    get kinderen(): FormArray {
        return this.kinderenFg.get('kinderen') as FormArray;
    }

    get hasChildrenYoungerThan13(): boolean {
        const today = new Date();
        return this.kinderenFg.get('kinderen')?.value.some((kind: Kind) => {
            const age = today.getFullYear() - new Date(kind.geboortedatum).getFullYear();
            const isBeforeBirthdayThisYear =
                today.getMonth() < new Date(kind.geboortedatum).getMonth() ||
                (today.getMonth() === new Date(kind.geboortedatum).getMonth() &&
                    today.getDate() < new Date(kind.geboortedatum).getDate());
            return age < 13 || (age === 13 && isBeforeBirthdayThisYear);
        }) ?? false;
    }

    public isJongerDan13Jaar(geboortedatum: Date): boolean {
        const yearThreshold = this.allowanceEntity!.year! - 13;
        const birthYear = new Date(geboortedatum).getFullYear();
        return birthYear >= yearThreshold;
    }



    getOpvangPeriodes(index: number): FormArray {
        const outerGroup = this.kinderen.at(index) as FormGroup;
        return outerGroup.get('opvangPerKind') as FormArray;
    }

    addOpvangPeriodes(index: number): void {
        const innerGroup = this.createOpvangPeriode();
        const outerGroup = this.kinderen.at(index) as FormGroup;
        const innerArray = outerGroup.get('opvangPerKind') as FormArray;
        innerArray.push(innerGroup);
    }

    deleteOpvangperiode(outerIndex: number, innerIndex: number): void {
        const innerArray = this.getOpvangPeriodes(outerIndex);
        innerArray.removeAt(innerIndex);
        this.kinderenFg.updateValueAndValidity()
        this.kinderenFg.markAsDirty()
        this.saveForm();
    }

    createOpvangPeriode(per?: OpvangPeriodes): FormGroup {
        return this.fb.group(({
            identificatienummerInstelling: per?.identificatienummerInstelling || undefined,
            naamKinderopvanginstelling: per?.naamKinderopvanginstelling || undefined,
            opvangVorm: per?.opvangVorm || undefined,
            periodeOpvang: this.fb.group<Periode>({
                begindatum: per?.periodeOpvang?.begindatum || undefined,
                einddatum: per?.periodeOpvang?.einddatum || undefined,
            }),
            soortOpvang: per?.soortOpvang || undefined,
            urenPerMaand: per?.urenPerMaand || undefined,
            uurprijs: per?.uurprijs || undefined,
        } as OpvangPeriodes));
    }

    createKindFormGroup(kind?: Kind): FormGroup {
        return this.fb.group({
            voornaam: kind ? kind.voornaam : null as unknown as string,
            geboortedatum: kind ? kind.geboortedatum : null as unknown as Date,
            bsn: kind ? kind.bsn : null as unknown as string,
            ingeschrevenOpAdresAangever: kind ? kind.ingeschrevenOpAdresAangever : true,
            ouderOntvangtKinderbijslag: kind ? kind.ouderOntvangtKinderbijslag : true,
            onderhoudBelangrijkeMate: kind ? kind.onderhoudBelangrijkeMate : false,
            opvangPerKind: this.fb.array<OpvangPeriodes>([]),
        });
    }

    public ngOnInit(): void {
        this.dataService.allowanceEntity.pipe(takeUntil(this.destroy$)).subscribe((value) => {
            this.allowanceEntity = value;
            this.year = value.year
            this.updateForm();
        });
        this.dataAlgService.client.pipe(takeUntil(this.destroy$)).subscribe((clientEntity) => {
            this.clientEntity = clientEntity;
        });
        this.kinderenFg.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
            this.formStructure = value;
        });
        this.dataService.hasKinderen.pipe(takeUntil(this.destroy$)).subscribe((value) => {
            this.hasKinderen = value;
        });
        this.router.events.pipe(
          takeUntil(this.destroy$),
          filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe(() => {
                this.handleUrlChange()
            });
    }

    private updateForm(): void {
        const lijst = this.allowanceEntity?.toeslagen?.kinderen || [];
        const formArray = this.kinderenFg.get('kinderen') as FormArray;
        const focusedControl = this.focusedElementId ? document.getElementById(this.focusedElementId) as HTMLElement : null;

        while (formArray.length > lijst.length) {
            formArray.removeAt(formArray.length - 1);
        }

        for (let i = 0; i < lijst.length; i++) {
            if (i < formArray.length) {
                const control = formArray.at(i) as FormGroup;
                control.patchValue(lijst[i]);
            } else {
                const formGroup = this.createKindFormGroup(lijst[i])
                formArray.push(formGroup);
            }
        }

        for (let i = 0; i < lijst.length; i++) {
            const kind = lijst[i];
            const opvangPeriode = kind.opvangPerKind || [];
            const opvangPeriodeFormArray = this.getOpvangPeriodes(i);

            while (opvangPeriodeFormArray.length > opvangPeriode.length) {
                opvangPeriodeFormArray.removeAt(opvangPeriodeFormArray.length - 1);
            }
            for (let j = 0; j < opvangPeriode.length; j++) {
                if (j < opvangPeriodeFormArray.length) {
                    const control = opvangPeriodeFormArray.at(j) as FormGroup;
                    control.patchValue(opvangPeriode[j]);
                } else {
                    const periode = this.createOpvangPeriode(opvangPeriode[j]);
                    opvangPeriodeFormArray.push(periode);
                }
            }
        }

        if (focusedControl && document.contains(focusedControl)) {
            focusedControl.focus();
        }
    }

    saveForm(): void {
        setTimeout(() => {
            this.kinderenFg.updateValueAndValidity();
            this.allowanceEntity.toeslagen = {
                ...this.allowanceEntity.toeslagen,
                kinderen: this.kinderenFg.value.kinderen
            } as AllowanceEntity
            this.dataService.updateAllowanceTaxEntity(this.allowanceEntity);
        }, 50)
    }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

    handleUrlChange() {
        if (!this.isSaving && this.hasUnsavedChanges()) {
            this.isSaving = true;
            this.tslLogic.postAllowances(this.clientEntity.sstRelationId!, this.allowanceEntity, false);
            this.isSaving = false;
        }
    }

    private hasUnsavedChanges(): boolean {
        return this.kinderenFg.dirty
    }
}
